import type { IconType } from '@kognitos/lattice';
import type { TFunction } from 'i18next';
import startCase from 'lodash/startCase';

/** How an agent's avatar is drawn: a short monogram or a glyph. */
export type TRainAgentAvatar =
  | { kind: 'monogram'; text: string }
  | { kind: 'icon'; icon: IconType };

/**
 * Semantic tone of an agent's avatar tile. `neutral` is the root/default look;
 * `brand` marks a specialist that took over the conversation.
 */
export type TRainAgentTone = 'neutral' | 'brand';

/** How an agent presents itself wherever the chat names it (transfer markers, indicators). */
export interface IRainAgentIdentity {
  agentId: string;
  /** Translated display name. */
  name: string;
  avatar: TRainAgentAvatar;
  tone: TRainAgentTone;
}

interface IRainAgentIdentityEntry {
  /** Key in the `rain` namespace for the agent's display name. */
  nameKey: string;
  avatar: TRainAgentAvatar;
  tone: TRainAgentTone;
}

/**
 * Known agents a thread can hand off between, keyed by rain agent id. Kept in
 * the core rather than under `./agents/*` because a transfer can name any agent,
 * not only the thread's root profile. Add an entry here (and its name to
 * `rain.json`) to give a new agent its own name and avatar; unknown ids still
 * resolve via {@link resolveRainAgentIdentity}.
 */
export const RAIN_AGENT_IDENTITIES: Readonly<
  Record<string, IRainAgentIdentityEntry>
> = {
  quill2: {
    nameKey: 'rain.agent.quill2',
    avatar: { kind: 'icon', icon: 'PenLine' },
    tone: 'neutral',
  },
  'browser-author': {
    nameKey: 'rain.agent.browserAgent',
    avatar: { kind: 'icon', icon: 'Globe' },
    tone: 'brand',
  },
};

/**
 * Resolves the display identity for a rain agent id, falling back to a
 * title-cased name and a one-letter monogram for agents not in
 * {@link RAIN_AGENT_IDENTITIES}.
 *
 * @param agentId - The rain agent id (e.g. `quill2`, `browser-author`)
 * @param t - Translator for the `rain` namespace
 * @returns The agent's display identity
 */
export const resolveRainAgentIdentity = (
  agentId: string,
  t: TFunction,
): IRainAgentIdentity => {
  const known = RAIN_AGENT_IDENTITIES[agentId];
  if (known) {
    const { nameKey, ...rest } = known;
    return { agentId, name: t(nameKey), ...rest };
  }
  const name = startCase(agentId) || agentId;
  return {
    agentId,
    name,
    avatar: { kind: 'monogram', text: name.charAt(0).toUpperCase() },
    tone: 'neutral',
  };
};
